import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';

interface DocumentPreviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documentUrl: string | null;
  onApproveAndSend: () => void;
  onBackToEdit: () => void;
  isLoading?: boolean;
  viewOnly?: boolean;
}

export const DocumentPreviewModal = ({
  open,
  onOpenChange,
  documentUrl,
  onApproveAndSend,
  onBackToEdit,
  isLoading = false,
  viewOnly = false,
}: DocumentPreviewModalProps) => {
  const [pdfError, setPdfError] = useState(false);

  const handlePdfError = () => {
    setPdfError(true);
  };

  const handlePdfLoad = () => {
    setPdfError(false);
  };

  // Add default zoom parameters and cache busting to PDF URL
  const getOptimizedPdfUrl = (url: string | null): string | undefined => {
    if (!url) return undefined;
    const timestamp = Date.now();
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}zoom=100&view=FitH&pagemode=none&t=${timestamp}`;
  };
  const optimizedDocumentUrl = getOptimizedPdfUrl(documentUrl);

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent className="sm:max-w-[95vw] sm:max-h-[95vh] bg-white">
        <DialogHeader>
          <DialogTitle>
            {viewOnly ? 'View Insertion Order' : 'Preview Insertion Order'}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-[70vh] bg-gray-50 rounded-md border border-gray-200">
          {(!documentUrl || pdfError) ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <p className="text-gray-500 mb-2">
                  No document preview available.
                </p>
                <p className="text-sm text-gray-400">
                  The document may still be generating. Please try again later.
                </p>
              </div>
            </div>
          ) : (
            <iframe
              src={optimizedDocumentUrl || undefined}
              className="w-full h-full rounded-md"
              title="Insertion Order Preview"
              onError={handlePdfError}
              onLoad={handlePdfLoad}
            />
          )}
        </div>

        {!viewOnly && (
          <DialogFooter className="flex gap-2 pt-4">
            <Button
              variant="outline"
              onClick={onBackToEdit}
              disabled={isLoading}
            >
              Back to Edit
            </Button>
            <Button
              className="bg-green-1000 text-white hover:bg-green-1000/90"
              onClick={onApproveAndSend}
              disabled={isLoading || !documentUrl}
            >
              {isLoading ? 'Processing...' : 'Approve & Send'}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};